import React, { useCallback } from 'react';
import { Icon } from '@vectord/ui';
import { throttle } from 'lodash';
import { useMediaQuery } from '@common/hooks';
import { GTMTracking } from '@common/ui/general';

import { PLACEMENT_BANNER_LINK } from '@alumni_v2/utils/constants';
import { downloadReport } from '@alumni_v2/api/placementReport';
import {
  isUserLoggedIn,
  authTrigger,
  setAuthPathAttribute,
} from '@alumni_v2/utils/helpers';

import placementReportMobileIcon from '@alumni_v2/images/webp/report-icon.webp';
import ThumbsUp from '@alumni_v2/images/svg/thumbs-up.svg';
import Ruppee from '@alumni_v2/images/svg/ruppee.svg';
import Suitcase from '@alumni_v2/images/svg/suitcase.svg';

import styles from './PlacementBanner.module.scss';


const PlacementBanner = () => {
  const { mobile } = useMediaQuery();

  const sendDataOnDownloadReport = useCallback(async () => {
    await downloadReport({ source: 'alumni_page' });
  }, []);

  const throttledDownloadReport = throttle(() => {
    sendDataOnDownloadReport();
  }, 5 * 60000, { leading: true });

  const openPlacemnetBrochure = useCallback(() => {
    if (isUserLoggedIn()) {
      throttledDownloadReport();
      window.open(PLACEMENT_BANNER_LINK, '_blank');
    } else {
      setAuthPathAttribute(PLACEMENT_BANNER_LINK);
      authTrigger();
    }
  }, [throttledDownloadReport]);

  const statsData = [
    {
      iconSrc: ThumbsUp,
      alt: 'thumbs-up-icon',
      count: '1.7 Cr',
      desc: 'Highest Package',
    },
    {
      iconSrc: Ruppee,
      alt: 'ruppee-icon',
      count: '150%',
      desc: 'Average Hike',
    },
    {
      iconSrc: Suitcase,
      alt: 'suitcase-icon',
      count: '96%',
      desc: 'Placement Rate',
    },
  ];

  return mobile ? (
    <GTMTracking
      component="div"
      gtmEventType="cta"
      gtmEventAction="click"
      gtmEventResult="download_audit_report"
      gtmEventCategory="alumni_directory"
      gtmEventData={{ is_logged_in: isUserLoggedIn() }}
      onClick={openPlacemnetBrochure}
      role="button"
      tabIndex={0}
      className={styles.placementBannerWrapper}
    >
      <div className={styles.placementReportContainer}>
        <img
          src={placementReportMobileIcon}
          className={styles.reportImage}
          alt="report-icon"
        />
        <div className={styles.placementReportDesc}>
          <div className={styles.placementReportText}>
            2024 Scaler Placement Assessment Report:
          </div>
          <div className={styles.reportButton}>
            <span className={styles.reportButtonText}>View Report Now</span>
            <span className={styles.caretIconNew}>
              <Icon name="caret-right" size="s" />
            </span>
          </div>
        </div>
      </div>
    </GTMTracking>
  ) : (
    <div className={styles.placementReportDesktop}>
      <div className={styles.placementReportDesktopTitle}>
        <img
          src={placementReportMobileIcon}
          className={styles.desktopReportImage}
          alt="report-icon"
        />
        <div className={styles.reportHeadingWrapper}>
          <div className={styles.desktopReportHeading}>
            Scaler Placement Report
          </div>
          <div className={styles.verificationWrapper}>
            <Icon
              name="circle-wavy-check"
              color="icon-neutral-inverse-strong"
              type="fill"
            />
            <div className={styles.verificationText}>
              Verified by B2K Analytics
            </div>
          </div>
        </div>
      </div>
      <div className={styles.placementReportDesktopStats}>
        {statsData.map((data) => (
          <div className={styles.statsContainer}>
            <div className={styles.statsNumber}>
              <img src={data.iconSrc} alt={data.alt} height={17} width={20} />
              <div className={styles.statsText}>{data.count}</div>
            </div>
            <div className={styles.statsDesc}>{data.desc}</div>
          </div>
        ))}
      </div>
      <GTMTracking
        component="div"
        gtmEventType="cta"
        gtmEventAction="click"
        gtmEventResult="download_audit_report"
        gtmEventCategory="alumni_directory"
        gtmEventData={{ is_logged_in: isUserLoggedIn() }}
        onClick={openPlacemnetBrochure}
        role="button"
        tabIndex={0}
        className={styles.downloadReportButton}
      >
        Download Now
      </GTMTracking>
    </div>
  );
};

export default PlacementBanner;
